import {
    type ApiResult,
    type BaseParams,
    type CreateCompetitionTeamParams,
    type GetCompetitionTeamParams,
    type GetTeamByInviteParams,
    type InviteCallParams,
    links,
    type Member,
    type PaginatedResult,
    Request,
    type SingleTeamParams,
    type Team,
    type UpdateTeamParams,
} from '@app/client';

import { apiReturnPaginatedResults, apiReturnResults } from '../../apiFactory/apiReturn';

export async function getUserTeams(params: BaseParams): Promise<ApiResult<PaginatedResult<Team>>> {
    return Request.getRequest<PaginatedResult<Team>>({
        ...params,
        target: links.teams.collection(),
    });
}

export async function createCompetitionTeam(params: CreateCompetitionTeamParams): Promise<ApiResult<Team>> {
    return Request.postRequest<Team>({
        ...params,
        target: links.teams.collection(),
    });
}

export async function getCompetitionTeam(params: GetCompetitionTeamParams): Promise<ApiResult<Team>> {
    return Request.getRequest<Team>({
        ...params,
        target: links.teams.single(params.id).team(),
    });
}

export async function leaveTeam(params: SingleTeamParams): Promise<ApiResult<Member>> {
    return Request.deleteRequest<Member>({
        ...params,
        target: links.teams.single(params.teamId).members(),
    });
}
export async function deleteTeam(params: SingleTeamParams): Promise<ApiResult<Team>> {
    return Request.deleteRequest<Team>({
        ...params,
        target: links.teams.single(params.teamId).team(),
    });
}
export async function updateTeam(params: UpdateTeamParams): Promise<ApiResult<Team>> {
    return Request.patchRequest<Team>({
        ...params,
        target: links.teams.single(params.teamId).team(),
    });
}

export async function joinTeam(params: InviteCallParams): Promise<ApiResult<Member>> {
    return Request.postRequest<Member>({
        target: links.teams.single(params.value.team_id).members(),
        ...params,
    });
}

export async function getCompetitionTeamByInvite(params: GetTeamByInviteParams): Promise<ApiResult<Team>> {
    return Request.getRequest<Team>({
        ...params,
        target: links.teams.referral(params.inviteCode),
    });
}

export async function trackReferral(
    params: BaseParams & { code: string; competitionId: string },
): Promise<ApiResult<unknown>> {
    return Request.postRequest<unknown>({
        target: links.fantasy.referral(params.code),
        value: { competitionId: params.competitionId },
        ...params,
    });
}

export const callTrackReferral = (params: BaseParams & { code: string; competitionId: string }) =>
    apiReturnResults<unknown>(trackReferral, params);

export const callGetCompetitionTeam = (params: GetCompetitionTeamParams) =>
    apiReturnResults<Team>(getCompetitionTeam, params);

export const callGetCompetitionTeamByInvite = (params: GetTeamByInviteParams) =>
    apiReturnResults<Team>(getCompetitionTeamByInvite, params);

export const callCreateCompetitionTeam = (params: CreateCompetitionTeamParams) =>
    apiReturnResults<Team>(createCompetitionTeam, params);

export const callJoinTeam = (params: InviteCallParams) => apiReturnResults<Member, string>(joinTeam, params);
export const callLeaveTeam = (params: SingleTeamParams) => apiReturnResults<Member>(leaveTeam, params);
export const callDeleteTeam = (params: SingleTeamParams) => apiReturnResults<Team>(deleteTeam, params);
export const callUpdateTeam = (params: UpdateTeamParams) => apiReturnResults<Team>(updateTeam, params);
export const callGetTeams = (params: BaseParams) => apiReturnPaginatedResults<Team>(getUserTeams, params);
